import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Briefcase, Building2, ShieldCheck, Sparkles, ChevronDown, UserRound, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const AUDIENCE = [
  { to: "/for-leder", icon: UserRound, key: "leder" },
  { to: "/for-hr", icon: Briefcase, key: "hr" },
  { to: "/for-bestyrelse", icon: Landmark, key: "board" },
  { to: "/for-kommune", icon: Building2, key: "municipality" },
  { to: "/for-it", icon: ShieldCheck, key: "it" },
  { to: "/enterprise", icon: Sparkles, key: "enterprise" },
] as const;

const PRIMARY = [
  { to: "/features", key: "features" },
  { to: "/science", key: "science" },
  { to: "/trust", key: "trust" },
  { to: "/pricing", key: "pricing" },
] as const;

export function PublicHeaderNav() {
  const { t } = useTranslation("ui");
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (to: string) => pathname === to || pathname.startsWith(to + "/");
  const audienceActive = AUDIENCE.some((a) => isActive(a.to));

  return (
    <>
      {/* Desktop nav */}
      <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
        {PRIMARY.map(({ to, key }) => (
          <Link
            key={to}
            to={to}
            className={cn(
              "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
              isActive(to)
                ? "text-primary bg-primary/10"
                : "text-foreground/70 hover:text-foreground hover:bg-accent",
            )}
          >
            {t(`nav.publicHeader.${key}`)}
          </Link>
        ))}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1",
                audienceActive
                  ? "text-primary bg-primary/10"
                  : "text-foreground/70 hover:text-foreground hover:bg-accent",
              )}
            >
              {t("nav.publicHeader.toWho")}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72 bg-background z-50">
            {AUDIENCE.map(({ to, icon: Icon, key }) => (
              <DropdownMenuItem key={to} asChild>
                <Link to={to} className="flex items-start gap-3 cursor-pointer py-2.5">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      {t(`nav.publicHeader.audience.${key}.title`)}
                    </p>
                    <p className="text-xs text-muted-foreground leading-snug mt-0.5">
                      {t(`nav.publicHeader.audience.${key}.desc`)}
                    </p>
                  </div>
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>

      {/* Mobile hamburger */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="lg:hidden" aria-label={t("nav.publicHeader.menu")}>
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-[88vw] max-w-sm p-0 bg-background">
          <div className="flex items-center justify-between border-b px-5 py-3">
            <span className="text-lg font-bold text-primary">Relark</span>
            <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} aria-label={t("nav.publicHeader.closeMenu")}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="overflow-y-auto px-3 py-4 max-h-[calc(100vh-3.5rem)]">
            <div className="space-y-0.5 mb-6">
              {PRIMARY.map(({ to, key }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "block rounded-md px-3 py-2.5 text-base font-medium",
                    isActive(to) ? "text-primary bg-primary/10" : "text-foreground hover:bg-accent",
                  )}
                >
                  {t(`nav.publicHeader.${key}`)}
                </Link>
              ))}
            </div>
            <p className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {t("nav.publicHeader.toWho")}
            </p>
            <div className="space-y-1 mb-6">
              {AUDIENCE.map(({ to, icon: Icon, key }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-start gap-3 rounded-md px-3 py-2.5 hover:bg-accent"
                >
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      {t(`nav.publicHeader.audience.${key}.title`)}
                    </p>
                    <p className="text-xs text-muted-foreground leading-snug mt-0.5">
                      {t(`nav.publicHeader.audience.${key}.desc`)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
            <div className="border-t pt-4 space-y-2">
              <Button asChild variant="outline" className="w-full">
                <Link to="/auth" onClick={() => setMobileOpen(false)}>
                  {t("nav.publicHeader.signIn")}
                </Link>
              </Button>
              <Button asChild className="w-full">
                <Link to="/onboarding" onClick={() => setMobileOpen(false)}>
                  {t("nav.publicHeader.startFree")}
                </Link>
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
